import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, MenuItem, Select, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import { IBatchStatusChangeRequest, IBatchWarning, IShipmentLight } from '../../types/shipment';

import { ErrorProvider } from '../../classes/errorProvider';
import { useUserContext } from '../../contexts/userContext';
import { BatchService } from '../../services/batchService';

import { Icons } from '../icons';
import Spinner from '../spinner';
import WorkingModal, { IWorkingModalProps } from '../workingModal';
import Json from '../json';

import '../../styles/entity/batchForms.scss';

export interface IBatchWorkflowForm {
	batchId: string;
	onBatchStatusUpdate?: (status: string) => void;
	areInputsDisabled?: boolean;
	isLoadingBatchShipments?: boolean;
	batchShipments?: IShipmentLight[];
}

const BatchWorkflowForm: React.FunctionComponent<IBatchWorkflowForm> = ({ batchId, areInputsDisabled, onBatchStatusUpdate, isLoadingBatchShipments, batchShipments }) => {
	const userContext = useUserContext();
	const [isLoadingStatus, setIsLoadingStatus] = useState(false);
	const [userCanEditStep, setUserCanEditStep] = useState(false);
	const [userCanEditBatches, setUserCanEditBatches] = useState(userContext?.permissions.canManageBatches);
	const [batchStatus, setBatchStatus] = useState<string | undefined>(undefined);
	const [stepNumber, setStepNumber] = useState(1);
	const [batchHasWarnings, setBatchHasWarnings] = useState(false);
	const [batchWarnings, setBatchWarnings] = useState<IBatchWarning[]>([]);
	const [showAllWarnings, setShowAllWarnings] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
	const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [showRejectWorkflowDialog, setShowRejectWorkflowDialog] = useState(false);
	const [rejectWorkflowReason, setRejectWorkflowReason] = useState('-');
	const [rejectWorkflowComment, setRejectWorkflowComment] = useState('');

	const noStepPermissionMessage = "* You do not have permission to make changes to shipments in this status.";
	const noBatchPermissionMessage = "* You do not have permission to make changes to shipment batches.";

	useEffect(() => {
		loadStatus();
	}, [batchId]);

	useEffect(() => {
		if (!isLoadingStatus && workingModalParams) {
			setWorkingModalParams(undefined);
		}
	}, [isLoadingStatus])

	const loadStatus = async () => {
		setIsLoadingStatus(true);

		BatchService.getStatus(batchId)
			.then((response) => {
				console.log('BatchService.getStatus: SUCCESS', response);
				if (batchStatus !== response.data.status) onBatchStatusUpdate?.(response.data.status);
				setBatchStatus(response.data.status);
				checkPermissions(response.data.status);
				checkWarnings(response);
			})
			.catch((error) => {
				console.log('BatchService.getStatus: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				setBatchStatus(undefined);
			})
			.finally(() => {
				setIsLoadingStatus(false);
			})
	}

	const checkPermissions = (status: string) => {
		switch (status.toLowerCase()) {
			case "reconciling": {
				if (userContext.permissions.canManageShipments) {
					setUserCanEditStep(true);
				}
				else {
					setUserCanEditStep(false);
				}
				setStepNumber(1);
				break;
			}
			case "escalated": {
				if (userContext.permissions.canApproveEscalatedShipments) {
					setUserCanEditStep(true);
				}
				else {
					setUserCanEditStep(false);
				}
				setStepNumber(2);
				break;
			}
			case "reviewing": {
				if (userContext.permissions.canReviewShipments) {
					setUserCanEditStep(true);
				}
				else {
					setUserCanEditStep(false);
				}
				setStepNumber(3);
				break;
			}
			case "auditing": {
				if (userContext.permissions.canCompleteShipments) {
					setUserCanEditStep(true);
				}
				else {
					setUserCanEditStep(false);
				}
				setStepNumber(4);
				break;
			}
			case "qb entry": {
				if (userContext.permissions.canQBEnter) {
					setUserCanEditStep(true);
				}
				else {
					setUserCanEditStep(false);
				}
				setStepNumber(5);
				break;
			}
			case "qb audit": {
				if (userContext.permissions.canQBAudit) {
					setUserCanEditStep(true);
				}
				else {
					setUserCanEditStep(false);
				}
				setStepNumber(6);
				break;
			}
			case "complete": {
				if (userContext.permissions.canUnlockFinalizedShipments) {
					setUserCanEditStep(true);
				}
				else {
					setUserCanEditStep(false);
				}
				setStepNumber(7);
				break;
			}
			default: {
				break;
			}
		}
	}

	const checkWarnings = (response: any) => {
		if (response.data.warnings.length > 0) {
			setBatchHasWarnings(true);//TODO: Change this when you get a real list of warnings from the API

			let warnings: IBatchWarning[] = [];
			response.data.warnings.map((item, index) => {
				let warning: IBatchWarning = {
					batchId: batchId,
					shipmentId: item.shipmentId,
					bolNumber: item.bolNumber,
					materialId: item.shipmentDetailId,
					text: item.message
				}

				warnings.push(warning);
			})
			setBatchWarnings(warnings);
			setBatchHasWarnings(true);
		}
		else {
			setBatchHasWarnings(false);
		}
	}

	const handleWorkflowAccept = (newStatus: string, backTrack: boolean = false) => {
		setErrorMessage(undefined);
		if (batchStatus === "Reconciling" && batchShipments?.length < 1) {
			setErrorMessage("Batch contains no shipments. Please add at least one shipment to advance from Reconciling status.");
			return;
		}

		setWorkingModalParams({
			params: {
				content:
					<div className="workflow-update">
						<div className="header">Updating Workflow:</div>
						<div className="content">
							{backTrack ? (
								<>{newStatus} <Icons.ArrowRight height="32px" className="back"></Icons.ArrowRight> {batchStatus}</>
							) : (
								<>{batchStatus} <Icons.ArrowRight height="32px" className="forward"></Icons.ArrowRight> {newStatus}</>
							)}
						</div>
					</div>
			}
		});

		const request: IBatchStatusChangeRequest = {
			id: parseInt(batchId),
			reason: backTrack ? rejectWorkflowReason : undefined,
			comment: backTrack ? rejectWorkflowComment : undefined,
			newStatus: newStatus,
			isBacktrack: backTrack
		}

		BatchService.setStatus(request)
			.then((response) => {
				loadStatus();
			})
			.catch((error) => {
				console.log('BatchService.setStatus: ERROR', error);
				setErrorMessage(ErrorProvider.getError(error));
				setWorkingModalParams(undefined);
			});
	}

	const openRejectWorkflowDialog = () => {
		setErrorMessage(undefined);
		setRejectWorkflowReason('-');
		setRejectWorkflowComment('');
		setShowRejectWorkflowDialog(true);
	}

	const handleRejectDialogCancel = () => {
		setErrorMessage(undefined);
		setShowRejectWorkflowDialog(false);
	}

	const handleRejectDialogReject = () => {
		if (rejectWorkflowReason == '-' || !rejectWorkflowComment?.trim()) {
			// UI should block this but check anyway
			return;
		}

		setShowRejectWorkflowDialog(false);
		handleWorkflowAccept("Reconciling", true);
	}

	const handleWorkflowReject = () => {
		openRejectWorkflowDialog();
	}

	return (
		<section className="hwi-batch-workflow-form">
			<Spinner dependency={(isLoadingStatus && !workingModalParams) || isLoadingBatchShipments || batchShipments == undefined} text='Loading Workflow ...'>
				{batchStatus &&
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<div className="workflow-wrapper">
							{/* <div className="title"><strong>Status: </strong>{batchStatus}</div> */}
							<div className="title">
								<div><strong>Status: </strong>{batchStatus}</div>
								<div className="progress">
									<div className={stepNumber >= 1 ? "on" : "off"}>{'\u2022 '}</div>
									<div className={stepNumber >= 2 ? "on" : "off"}>{'\u2022 '}</div>
									<div className={stepNumber >= 3 ? "on" : "off"}>{'\u2022 '}</div>
									<div className={stepNumber >= 4 ? "on" : "off"}>{'\u2022 '}</div>
									<div className={stepNumber >= 5 ? "on" : "off"}>{'\u2022 '}</div>
									<div className={stepNumber >= 6 ? "on" : "off"}>{'\u2022 '}</div>
									<div className={stepNumber >= 7 ? "on" : "off"}>{'\u2022 '}</div>
								</div>

							</div>
							{userCanEditBatches && !userCanEditStep &&
								<div className="no-permission">
									<p className="text-help">
										{noStepPermissionMessage}
									</p>
								</div>
							}
							{!userCanEditBatches &&
								<div className="no-permission">
									<p className="text-help">
										{noBatchPermissionMessage}
									</p>
								</div>
							}
							{userCanEditBatches && userCanEditStep &&
								<>
									<div className="spacer">{/* Comments used to be here. Adding spacer to keep the buttons their original size */}</div>
									<div className="actions">
										{!batchStatus &&
											<p><CircularProgress color="primary" size={16} /> Checking status...{"  "}</p>
										}
										{batchStatus?.toLowerCase() == "legacy" &&
											<Button variant="contained" color='error' disabled={areInputsDisabled} onClick={() => handleWorkflowAccept("Reconciling")}>Put in Reconciling Status</Button>
										}
										{batchStatus?.toLowerCase() == "reconciling" &&
											<>
												{!batchHasWarnings ? (
													<Button variant="contained" disabled={areInputsDisabled} onClick={() => handleWorkflowAccept("Reviewing")}>Submit</Button>
												) : (
													<Button variant="contained" disabled={areInputsDisabled} onClick={() => handleWorkflowAccept("Escalated")}>Escalate</Button>
												)}
											</>
										}
										{batchStatus?.toLowerCase() == "escalated" &&
											<>
												{userContext?.permissions.canApproveEscalatedShipments ? (
													<>
														<Button variant="contained" disabled={areInputsDisabled} onClick={() => handleWorkflowAccept("Reviewing")}>Submit</Button>
														<Button variant="contained" color='error' disabled={areInputsDisabled} onClick={() => handleWorkflowReject()}>Reject</Button>
													</>
												) : (
													<p className="text-help text-right">
														{noStepPermissionMessage}
													</p>
												)}
											</>
										}
										{batchStatus?.toLowerCase() == "reviewing" &&
											<>
												{userContext?.permissions.canReviewShipments ? (
													<>
														<Button variant="contained" disabled={areInputsDisabled} onClick={() => handleWorkflowAccept("Auditing")}>Approve</Button>
														<Button variant="contained" color='error' disabled={areInputsDisabled} onClick={() => handleWorkflowReject()}>Reject</Button>
													</>
												) : (
													<p className="text-help">
														{noStepPermissionMessage}
													</p>
												)}
											</>
										}
										{batchStatus?.toLowerCase() == "auditing" &&
											<>
												{userContext?.permissions.canCompleteShipments ? (
													<>
														<Button variant="contained" disabled={areInputsDisabled} onClick={() => handleWorkflowAccept("QB Entry")}>Approve</Button>
														<Button variant="contained" color='error' disabled={areInputsDisabled} onClick={() => handleWorkflowReject()}>Reject</Button>
													</>
												) : (
													<p className="text-help">
														{noStepPermissionMessage}
													</p>
												)}
											</>
										}
										{batchStatus?.toLowerCase() == "qb entry" &&
											<>
												{userContext?.permissions.canQBEnter ? (
													<>
														<Button variant="contained" disabled={areInputsDisabled} onClick={() => handleWorkflowAccept("QB Audit")}>Submit</Button>
														<Button variant="contained" color='error' disabled={areInputsDisabled} onClick={() => handleWorkflowReject()}>Reject</Button>
													</>
												) : (
													<p className="text-help">
														{noStepPermissionMessage}
													</p>
												)}
											</>
										}
										{batchStatus?.toLowerCase() == "qb audit" &&
											<>
												{userContext?.permissions.canQBAudit ? (
													<>
														<Button variant="contained" disabled={areInputsDisabled} onClick={() => handleWorkflowAccept("Complete")}>Complete</Button>
														<Button variant="contained" color='error' disabled={areInputsDisabled} onClick={() => handleWorkflowReject()}>Reject</Button>
													</>
												) : (
													<p className="text-help">
														{noStepPermissionMessage}
													</p>
												)}
											</>
										}
										{batchStatus?.toLowerCase() == "complete" &&
											<>
												{userContext?.permissions.canUnlockFinalizedShipments ? (
													<>
														<Button variant="contained" color='error' disabled={areInputsDisabled} onClick={() => handleWorkflowReject()}>Unlock</Button>
													</>
												) : (
													<p className="text-help">
														{noStepPermissionMessage}
													</p>
												)}
											</>
										}
										{batchWarnings &&
											<Button variant="contained" color='warning' onClick={() => setShowAllWarnings(!showAllWarnings)}><Icons.Warning fill="#fff" height="16px"></Icons.Warning>{`(${batchWarnings.length})`}</Button>
										}
									</div>

								</>
							}

						</div>
						{showAllWarnings &&
							<div className="warnings">
								<Alert className="alert-warning-main"
									severity="warning"
								>
									<p className="main-warning">This shipment has a total of <strong>{batchWarnings.length} warnings.</strong></p>
									{showAllWarnings &&
										<>
											{batchWarnings.map((item, index) => (
												<p className="sub-warning"><strong>{'\u2022 '}{item.bolNumber}{item.materialId ? ` / Material ${item.materialId}` : ""}:</strong> {item.text}</p>
											))}
										</>
									}
								</Alert>

							</div>
						}
					</LocalizationProvider>
				}
				{errorMessage &&
					<Alert className="alert-error"
						severity="error"
						action={<IconButton onClick={() => { setErrorMessage(undefined); }} size='small'>X</IconButton>}
					>
						{errorMessage}
					</Alert>
				}
				{successMessage &&
					<Alert className="alert-success"
						severity="success"
						action={<IconButton onClick={() => { setSuccessMessage(undefined); }} size='small'>X</IconButton>}>
						{successMessage}
					</Alert>
				}
			</Spinner>
			<Dialog
				fullScreen={false}
				open={showRejectWorkflowDialog}
				onClose={() => { /* Make them click cancel instead of just any stray clicks outside the dialog */ }}
				aria-labelledby='workflow-reject-dialog'
				className='workflow-reject-dialog'
				maxWidth='sm'
				fullWidth={true}>
				<DialogTitle className="title">
					Reject {batchStatus} Status
				</DialogTitle>
				<DialogContent className="content">
					<DialogContentText className="text">
						<p>Please select a reason for rejecting {batchStatus} status and include comments with any relevant details.</p>
					</DialogContentText>
					<div className="fields">
						<Select
							className={`reason${rejectWorkflowReason == '-' ? ' unselected' : ''}`}
							value={rejectWorkflowReason}
							onChange={(e) => setRejectWorkflowReason(e.target.value)}
						>
							<MenuItem value="-" disabled>-- Select a reason --</MenuItem>
							<MenuItem value="Volume correction needed">Volume correction needed</MenuItem>
							<MenuItem value="Material correction needed">Material correction needed</MenuItem>
							<MenuItem value="Value ($) correction needed">Value ($) correction needed</MenuItem>
							<MenuItem value="Supportive documents correction needed">Supportive documents correction needed</MenuItem>
							<MenuItem value="Duplicate correction needed">Duplicate correction needed</MenuItem>
							<MenuItem value="Other">Other</MenuItem>
						</Select>
						<TextField
							className='comment'
							placeholder="Comment (required)"
							multiline
							rows={4}
							value={rejectWorkflowComment}
							onChange={(e) => setRejectWorkflowComment(e.target.value)}
						>
						</TextField>
					</div>
				</DialogContent>
				<DialogActions className="actions">
					<Button variant="contained" color="primary" className="reject" disabled={rejectWorkflowReason == '-' || !rejectWorkflowComment?.trim()} onClick={() => handleRejectDialogReject()}>
						Reject
					</Button>
					<Button variant="contained" color="error" className="cancel" onClick={() => handleRejectDialogCancel()}>
						Cancel
					</Button>
				</DialogActions>
			</Dialog>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
		</section>
	)
}

export default BatchWorkflowForm;